import { useState, useEffect } from "react";
import { Shield, ChevronDown, ChevronUp, Search, AlertTriangle, CheckCircle, Lock } from "lucide-react";
import { useAuthStore } from "../store/authStore";
import UpgradeModal from "../components/UpgradeModal";

const API_URL = import.meta.env.VITE_API_URL || "http://64.23.240.10/api/v1";
const BASE_API_URL = API_URL.replace("/v1", "");

interface Assessment {
  id: number;
  protocol_name: string;
  protocol_key: string;
  overall_risk_score: number;
  risk_tier: string;
  audit_score: number;
  tvl_score: number;
  team_score: number;
  yield_sustainability_score: number;
  audit_status: string;
  audit_firm: string;
  audit_findings_summary: string;
  team_doxxed: boolean;
  team_background: string;
  tvl_usd: number;
  tvl_trend: string;
  days_operational: number;
  incident_history: string;
  yield_source: string;
  yield_sustainable: boolean;
  red_flags: string[];
  green_flags: string[];
  assessor_notes: string;
}

const TIER_COLORS: Record<string, string> = {
  low: "bg-green-500/20 text-green-400 border-green-500/30",
  moderate: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
  elevated: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  high: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  critical: "bg-red-500/20 text-red-400 border-red-500/30",
};

const TIER_BG: Record<string, string> = {
  low: "bg-green-500",
  moderate: "bg-cyan-500",
  elevated: "bg-yellow-500",
  high: "bg-orange-500",
  critical: "bg-red-500",
};

function tierBadge(tier: string) {
  return (
    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold border ${TIER_COLORS[tier] || "bg-gray-500/20 text-gray-400 border-gray-500/30"}`}>
      {(tier || "N/A").toUpperCase()}
    </span>
  );
}

function formatTvl(val: number) {
  if (!val) return "N/A";
  if (val >= 1e12) return "$" + (val / 1e12).toFixed(1) + "T";
  if (val >= 1e9) return "$" + (val / 1e9).toFixed(1) + "B";
  if (val >= 1e6) return "$" + (val / 1e6).toFixed(0) + "M";
  if (val >= 1e3) return "$" + (val / 1e3).toFixed(0) + "K";
  return "$" + val;
}

function DimensionBar({ label, score, max }: { label: string; score: number; max: number }) {
  const pct = (score / max) * 100;
  let color = "bg-green-500";
  if (pct < 40) color = "bg-red-500";
  else if (pct < 70) color = "bg-yellow-500";
  else if (pct < 85) color = "bg-cyan-500";
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="text-white/60 w-40 shrink-0">{label}</span>
      <div className="flex-1 bg-steel-blue/20 rounded-full h-2.5 overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-white/80 font-mono w-12 text-right">{score}/{max}</span>
    </div>
  );
}

export default function RiskAssessmentPage() {
  const { isAuthenticated, user, token } = useAuthStore();
  const tier = user?.subscriptionTier || "free";
  const canExpand = isAuthenticated && tier !== "free";

  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterTier, setFilterTier] = useState("all");
  const [sortBy, setSortBy] = useState("score_desc");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showExplainer, setShowExplainer] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const [requestName, setRequestName] = useState("");
  const [requestUrl, setRequestUrl] = useState("");
  const [requestMsg, setRequestMsg] = useState("");
  const [requestSubmitting, setRequestSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${BASE_API_URL}/risk/assessments`)
      .then((r) => r.json())
      .then((d) => setAssessments(d.assessments || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = assessments
    .filter((a) => filterTier === "all" || a.risk_tier === filterTier)
    .sort((a, b) => {
      if (sortBy === "score_desc") return b.overall_risk_score - a.overall_risk_score;
      if (sortBy === "score_asc") return a.overall_risk_score - b.overall_risk_score;
      if (sortBy === "tvl") return (b.tvl_usd || 0) - (a.tvl_usd || 0);
      return 0;
    });

  function handleExpand(id: number) {
    if (!canExpand) {
      setUpgradeOpen(true);
      if (isAuthenticated && tier === "free") {
        fetch(`${BASE_API_URL}/free-tier/limit-hit`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ feature: "history" }),
        }).catch(() => {});
      }
      return;
    }
    setExpandedId(expandedId === id ? null : id);
  }

  async function submitRequest() {
    if (!requestName.trim()) return;
    setRequestSubmitting(true);
    try {
      const res = await fetch(`${BASE_API_URL}/risk/request-assessment`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ protocol_name: requestName, protocol_url: requestUrl }),
      });
      const data = await res.json();
      setRequestMsg(data.message || "Request submitted.");
      setRequestName("");
      setRequestUrl("");
    } catch {
      setRequestMsg("Failed to submit request.");
    } finally {
      setRequestSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-dark">
      <UpgradeModal isOpen={upgradeOpen} onClose={() => setUpgradeOpen(false)} feature="history" currentLimit={12} />

      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-sky-blue/10 border border-sky-blue/20 mx-auto mb-6">
            <Shield size={32} className="text-sky-blue" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-3">Protocol Risk Intelligence</h1>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Every staking protocol assessed across four safety dimensions. Know what you are staking before you commit capital.
          </p>
        </div>

        {/* Explainer */}
        <div className="mb-8">
          <button
            onClick={() => setShowExplainer(!showExplainer)}
            className="flex items-center gap-2 text-sky-blue hover:text-sky-blue/80 text-sm font-medium transition"
          >
            {showExplainer ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            How the risk score is calculated
          </button>
          {showExplainer && (
            <div className="mt-4 p-6 bg-navy-dark border border-sky-blue/20 rounded-xl text-sm text-white/70 space-y-3">
              <p>Each protocol is scored across <strong className="text-white">4 dimensions</strong>, each worth 0–25 points, for a total of 0–100:</p>
              <ul className="space-y-1 ml-4 list-disc">
                <li><strong className="text-white">Audit Quality (0–25):</strong> Who audited, how recently, and whether findings were resolved.</li>
                <li><strong className="text-white">TVL &amp; Market Trust (0–25):</strong> Total Value Locked as a proxy for market confidence.</li>
                <li><strong className="text-white">Team &amp; Transparency (0–25):</strong> Whether the team is doxxed, their track record, and governance maturity.</li>
                <li><strong className="text-white">Yield Sustainability (0–25):</strong> Whether yield comes from organic network activity or temporary incentives.</li>
              </ul>
              <p className="mt-2">
                <strong className="text-white">Risk tiers:</strong>{" "}
                <span className="text-green-400">Low (85–100)</span> ·{" "}
                <span className="text-cyan-400">Moderate (70–84)</span> ·{" "}
                <span className="text-yellow-400">Elevated (50–69)</span> ·{" "}
                <span className="text-orange-400">High (30–49)</span> ·{" "}
                <span className="text-red-400">Critical (0–29)</span>
              </p>
              <p>Red flags (e.g., APY &gt;3x network average, &gt;30% TVL decline) automatically downgrade the tier by one level.</p>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-8">
          <select
            value={filterTier}
            onChange={(e) => setFilterTier(e.target.value)}
            className="bg-steel-blue/10 border border-sky-blue/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-sky-blue/50"
          >
            <option value="all">All Risk Tiers</option>
            <option value="low">Low Risk</option>
            <option value="moderate">Moderate Risk</option>
            <option value="elevated">Elevated Risk</option>
            <option value="high">High Risk</option>
            <option value="critical">Critical Risk</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-steel-blue/10 border border-sky-blue/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-sky-blue/50"
          >
            <option value="score_desc">Score: High to Low</option>
            <option value="score_asc">Score: Low to High</option>
            <option value="tvl">TVL: High to Low</option>
          </select>

          <div className="ml-auto text-white/40 text-sm self-center">
            {filtered.length} protocol{filtered.length !== 1 ? "s" : ""} assessed
          </div>
        </div>

        {/* Protocol Cards */}
        <div className="space-y-4">
          {filtered.map((a) => (
            <div key={a.id} className="bg-navy-dark border border-sky-blue/15 rounded-xl overflow-hidden hover:border-sky-blue/30 transition">
              <div className="p-5 flex flex-col md:flex-row md:items-center gap-4">
                {/* Score circle */}
                <div className="flex-shrink-0 flex items-center justify-center w-16 h-16 rounded-xl bg-steel-blue/10 border border-sky-blue/20">
                  <span className="text-2xl font-bold text-white">{a.overall_risk_score}</span>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-lg font-bold text-white truncate">{a.protocol_name}</h3>
                    {tierBadge(a.risk_tier)}
                  </div>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-white/50">
                    <span>TVL: {formatTvl(a.tvl_usd)}</span>
                    {a.days_operational && <span>{Math.floor(a.days_operational / 365)}+ years operational</span>}
                  </div>
                </div>

                {/* Dimension mini bars */}
                <div className="hidden lg:flex gap-1.5 items-end h-12">
                  {[
                    { score: a.audit_score, label: "A" },
                    { score: a.tvl_score, label: "T" },
                    { score: a.team_score, label: "Tm" },
                    { score: a.yield_sustainability_score, label: "Y" },
                  ].map((d, i) => (
                    <div key={i} className="flex flex-col items-center gap-0.5">
                      <div
                        className={`w-4 rounded-sm ${TIER_BG[a.risk_tier] || "bg-gray-500"}`}
                        style={{ height: `${(d.score / 25) * 40}px`, opacity: 0.7 }}
                      />
                      <span className="text-[9px] text-white/30">{d.label}</span>
                    </div>
                  ))}
                </div>

                {/* Flags preview */}
                <div className="flex gap-2 items-center">
                  {a.red_flags && a.red_flags.length > 0 && (
                    <span className="flex items-center gap-1 text-xs text-red-400">
                      <AlertTriangle size={12} /> {a.red_flags.length}
                    </span>
                  )}
                  {a.green_flags && a.green_flags.length > 0 && (
                    <span className="flex items-center gap-1 text-xs text-green-400">
                      <CheckCircle size={12} /> {a.green_flags.length}
                    </span>
                  )}
                </div>

                {/* Expand */}
                <button
                  onClick={() => handleExpand(a.id)}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-sky-blue hover:text-sky-blue/80 transition"
                >
                  {!canExpand && <Lock size={14} />}
                  {expandedId === a.id ? "Collapse" : "Details"}
                  {expandedId === a.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              </div>

              {/* Expanded detail */}
              {expandedId === a.id && canExpand && (
                <div className="border-t border-sky-blue/10 p-6 space-y-5 bg-steel-blue/5">
                  {/* Dimension scores */}
                  <div className="space-y-2.5">
                    <DimensionBar label="Audit Quality" score={a.audit_score} max={25} />
                    <DimensionBar label="TVL & Market Trust" score={a.tvl_score} max={25} />
                    <DimensionBar label="Team & Transparency" score={a.team_score} max={25} />
                    <DimensionBar label="Yield Sustainability" score={a.yield_sustainability_score} max={25} />
                  </div>

                  <div className="grid md:grid-cols-2 gap-5 text-sm">
                    {/* Audit */}
                    <div>
                      <h4 className="text-white font-semibold mb-1">Audit</h4>
                      <p className="text-white/60">{a.audit_firm || "N/A"}</p>
                      {a.audit_findings_summary && <p className="text-white/50 mt-1">{a.audit_findings_summary}</p>}
                    </div>
                    {/* Team */}
                    <div>
                      <h4 className="text-white font-semibold mb-1">Team</h4>
                      <p className="text-white/60">{a.team_doxxed ? "Doxxed" : "Pseudonymous/Anonymous"}</p>
                      {a.team_background && <p className="text-white/50 mt-1">{a.team_background}</p>}
                    </div>
                    {/* Yield */}
                    <div>
                      <h4 className="text-white font-semibold mb-1">Yield Source</h4>
                      <p className="text-white/60">{a.yield_source || "N/A"}</p>
                      <p className="text-white/50 mt-1">Sustainable: {a.yield_sustainable ? "Yes" : "Unclear"}</p>
                    </div>
                    {/* History */}
                    <div>
                      <h4 className="text-white font-semibold mb-1">Incident History</h4>
                      <p className="text-white/60">{a.incident_history || "No incidents recorded"}</p>
                    </div>
                  </div>

                  {/* Flags */}
                  <div className="flex flex-wrap gap-4">
                    {a.red_flags && a.red_flags.length > 0 && (
                      <div>
                        <h4 className="text-red-400 font-semibold text-sm mb-1 flex items-center gap-1"><AlertTriangle size={14} /> Red Flags</h4>
                        <ul className="text-red-400/80 text-xs space-y-0.5">
                          {a.red_flags.map((f, i) => <li key={i}>• {f}</li>)}
                        </ul>
                      </div>
                    )}
                    {a.green_flags && a.green_flags.length > 0 && (
                      <div>
                        <h4 className="text-green-400 font-semibold text-sm mb-1 flex items-center gap-1"><CheckCircle size={14} /> Green Flags</h4>
                        <ul className="text-green-400/80 text-xs space-y-0.5">
                          {a.green_flags.map((f, i) => <li key={i}>• {f}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>

                  {a.assessor_notes && (
                    <div className="bg-steel-blue/10 rounded-lg p-3 text-xs text-white/50 italic">
                      {a.assessor_notes}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Request Assessment */}
        {isAuthenticated && (
          <div className="mt-12 bg-navy-dark border border-sky-blue/20 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Search size={18} className="text-sky-blue" /> Request a Protocol Assessment
            </h3>
            <p className="text-white/50 text-sm mb-4">
              Do not see a protocol you are interested in? Request an assessment and we will evaluate it within 5 business days.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                placeholder="Protocol name"
                value={requestName}
                onChange={(e) => setRequestName(e.target.value)}
                className="flex-1 bg-steel-blue/10 border border-sky-blue/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/30"
              />
              <input
                type="text"
                placeholder="Protocol URL (optional)"
                value={requestUrl}
                onChange={(e) => setRequestUrl(e.target.value)}
                className="flex-1 bg-steel-blue/10 border border-sky-blue/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/30"
              />
              <button
                onClick={submitRequest}
                disabled={requestSubmitting || !requestName.trim()}
                className="px-6 py-2.5 bg-sky-blue hover:bg-sky-blue/90 disabled:opacity-40 text-navy-dark font-bold rounded-xl transition text-sm whitespace-nowrap"
              >
                {requestSubmitting ? "Submitting..." : "Request Assessment"}
              </button>
            </div>
            {requestMsg && <p className="text-sky-blue text-sm mt-3">{requestMsg}</p>}
          </div>
        )}

        {!isAuthenticated && (
          <div className="mt-12 text-center">
            <p className="text-white/50 text-sm mb-2">Sign in to request assessments for protocols not listed here.</p>
          </div>
        )}
      </div>
    </div>
  );
}
